import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { UserPlus, Clock } from '../../icons';
// 채택 3단계 ② 인사 — 손으로 짠 검색칸을 정본 SearchInput 으로(돋보기·지우기·Esc 가 딸려 온다).
import { SearchInput } from '../../components/shared';
import { HR_HOME_PATH, filterHrMenu, hrMenuFor } from '../../lib/hr-menu';
import { useIsAdminViewer } from '../../lib/menu-catalog';
import { useHrEmployeesGrant } from '../../lib/hr-grant';
import { getMyProfile } from '../../api/users';
// 2열 묶음 배치 · 접기 · 지금 위치를 화면 안에 (2026-10-17 · web 레인 W5 — lib/menu-layout.ts 머리말)
import { ChevronRight } from '../../icons';
import { HR_ADMIN_LAYOUT, isFolded, regroup } from '../../lib/menu-layout';
import { useActiveIntoView } from '../../hooks/useActiveIntoView';
import './HrShell.css';

/**
 * 인사 셸 — 좌 240px 트리 + 우 화면(`<Outlet/>`).
 *
 * ── 무엇이 바뀌나 ────────────────────────────────────────────────────────
 * `/erp/hr/*` 화면 열둘은 **이미 있었다**. 근태 1~6단계에서 만든 것이고, 지금까지는
 * 메뉴가 없어 «내 근태» 화면 아래 링크 목록으로만 갈 수 있었다. 이 셸은 그 화면들을
 * 한 지붕 아래로 묶는다 — 자식 라우트의 **element 는 한 줄도 바뀌지 않고**, 주소도 그대로다
 * (`router/index.tsx` 에서 같은 경로가 부모 'erp/hr' + 자식으로 나뉘었을 뿐이다).
 *
 * ── 누가 무엇을 보나 (2026-08-26) ────────────────────────────────────────
 * 셸은 하나이고 **트리만 갈라진다** — 회사 관리자는 담당자 트리(«인사»), 직원은 «내 인사».
 * 시안이 두 화면을 «같은 껍데기에 범위만 나» 로 그린 것을 그대로 따른다.
 *
 * 담당자 화면들은 저마다 `RoleGuard(ADMIN·SYSTEM_ADMIN)` 를 갖고 있어, 직원이 주소로 들어와도
 * 대시보드로 돌아간다. 그래서 **셸에는 가드를 두지 않는다** — 두면 직원이 «내 인사» 에도
 * 못 들어온다. 프론트 가드는 UX 게이트이며 권위 있는 차단은 서버가 한다.
 */
export function HrShell() {
  const { pathname } = useLocation();
  const isAdmin = useIsAdminViewer();
  const [query, setQuery] = useState('');
  /**
   * 맡은 조직이 있는가 — 「맡은 조직」 묶음이 붙는 조건(2026-08-29 · 사용자 지적).
   * ★ **판정은 서버가 한다**(`orgScope`). 못 읽으면 거짓 — **모르면 안 보여 주는 쪽**이다.
   *   할 수 없는 일을 메뉴에 세워 두면 그 자체가 잘못된 안내다.
   */
  const [hasOrg, setHasOrg] = useState(false);
  // 인사 메뉴를 받은 STAFF(인사 담당자) — 「직원 관리」 를 트리에 싣는다. 라우터 가드(HrGrantGuard)와 **같은 답**(모듈 캐시)이다.
  const employeesGranted = useHrEmployeesGrant() === true;
  const menu = useMemo(() => (isAdmin ? regroup(hrMenuFor(isAdmin, hasOrg, employeesGranted), HR_ADMIN_LAYOUT) : hrMenuFor(isAdmin, hasOrg, employeesGranted)), [isAdmin, hasOrg, employeesGranted]);
  const groups = useMemo(() => filterHrMenu(menu, query), [menu, query]);
  useActiveIntoView('.hrnav', pathname);
  // 접기(담당자 보기) — 지금 위치가 없는 묶음은 한 줄. 손으로 편 묶음은 이 셸 안에서만 기억한다.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const me = await getMyProfile();
        if (alive) setHasOrg((me.orgScope ?? 'NONE') !== 'NONE');
      } catch {
        if (alive) setHasOrg(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  const hit = groups.some((g) => g.items.length > 0);

  /** 인사 홈만 정확히 일치로 켠다 — 아니면 모든 하위 화면에서 홈까지 함께 켜진다. */
  const isOn = (to: string) =>
    to === HR_HOME_PATH ? pathname === HR_HOME_PATH : pathname === to || pathname.startsWith(`${to}/`);
  const activeGroup = groups.find((g) => g.items.some((i) => isOn(i.to)))?.label;
  const foldedOf = (label: string) => isFolded(label, { enabled: isAdmin, query, activeGroup, opened });

  return (
    <div className="hrshell">
      <nav className="hrnav" aria-label="인사 메뉴">
        <div className="hrnav__head">
          <h2 className="hrnav__title">{isAdmin ? '인사' : '내 인사'}</h2>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            placeholder="인사 메뉴 검색"
            aria-label="인사 메뉴 검색"
            fullWidth
          />
        </div>

        {/* 대표 행동 — 그 사람이 여기서 가장 자주 하는 일 하나.
            담당자는 입사 처리(직원 명부 화면이 갖고 있다), 직원은 출퇴근(내 근태)이다. */}
        {isAdmin ? (
          <Link className="hrnav__act" to="/erp/hr/employees/new">
            {/* 2026-09-13 · H — 입사 처리가 제 화면(`/erp/hr/employees/new`)으로 갈렸다(관제 결정). */}
            <UserPlus size={18} aria-hidden />
            입사 처리
          </Link>
        ) : (
          <Link className="hrnav__act" to="/erp/attendance">
            <Clock size={18} aria-hidden />
            출퇴근 찍기
          </Link>
        )}

        {groups.map((g) => (
          <div className="hrnav__group" key={g.label}>
            {foldedOf(g.label) && (
              <button
                type="button"
                className="hrnav__fold"
                aria-expanded={false}
                aria-label={`${g.label} 펴기`}
                onClick={() => setOpened((prev) => new Set([...prev, g.label]))}
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            )}
            <div className="hrnav__glabel">{g.label}</div>
            <div className="hrnav__groupitems" hidden={foldedOf(g.label) || undefined}>
            {g.items.map((i) => {
              const Icon = i.icon;
              return (
                <Link
                  key={i.to}
                  to={i.to}
                  className={`hrnav__item${isOn(i.to) ? ' is-on' : ''}`}
                  aria-current={isOn(i.to) ? 'page' : undefined}
                >
                  <Icon size={17} aria-hidden />
                  <span className="hrnav__nm">{i.label}</span>
                  <span className="hrnav__hint">{i.hint}</span>
                </Link>
              );
            })}
            </div>
          </div>
        ))}
        {!hit && <p className="hrnav__none">찾는 메뉴가 없습니다.</p>}
      </nav>

      <div className="hrnav__body">
        <Outlet />
      </div>
    </div>
  );
}
